import React, { useState } from "react";
import {
  Zap,
  Flame,
  Droplet,
  Waves,
  CloudRain,
  Recycle,
  AlertTriangle,
  Building,
  HelpCircle,
  Copy,
  CheckCircle2,
  Calendar,
} from "lucide-react";

// Utility details selector mapping labels to standard Lucide icons & styling
function getUtilityDetails(label) {
  const lbl = label.toLowerCase();
  if (lbl.includes("electricity")) {
    return {
      name: "Electricity Delivery & Energy",
      sub: "ELECTRICITY",
      icon: Zap,
      iconColor: "text-blue-500 bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/20",
    };
  }
  if (lbl.includes("natural gas") || lbl.includes("gas")) {
    return {
      name: "Natural Gas Delivery & Supply",
      sub: "GAS",
      icon: Flame,
      iconColor: "text-amber-500 bg-amber-50 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900/20",
    };
  }
  if (lbl.includes("wastewater")) {
    return {
      name: "Wastewater Collection & Treatment",
      sub: "WASTEWATER",
      icon: Waves,
      iconColor: "text-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 border-indigo-100 dark:border-indigo-900/20",
    };
  }
  if (lbl.includes("stormwater")) {
    return {
      name: "Stormwater Management Services",
      sub: "STORMWATER",
      icon: CloudRain,
      iconColor: "text-teal-500 bg-teal-50 dark:bg-teal-950/30 border-teal-100 dark:border-teal-900/20",
    };
  }
  if (lbl.includes("water")) {
    // Check water after wastewater/stormwater to prevent false matches
    return {
      name: "Water Treatment & Supply",
      sub: "WATER",
      icon: Droplet,
      iconColor: "text-sky-500 bg-sky-50 dark:bg-sky-950/30 border-sky-100 dark:border-sky-900/20",
    };
  }
  if (lbl.includes("waste") || lbl.includes("recycling")) {
    return {
      name: "Waste & Recycling Services",
      sub: "WASTE & RECYCLING",
      icon: Recycle,
      iconColor: "text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-900/20",
    };
  }
  if (lbl.includes("late payment") || lbl.includes("penalty")) {
    return {
      name: "Late Payment Charge",
      sub: "LATE PAYMENT",
      icon: AlertTriangle,
      iconColor: "text-rose-500 bg-rose-50 dark:bg-rose-950/30 border-rose-100 dark:border-rose-900/20",
    };
  }
  return {
    name: label,
    sub: "UTILITY",
    icon: HelpCircle,
    iconColor: "text-gray-500 bg-gray-50 dark:bg-gray-950/30 border-gray-100 dark:border-gray-900/20",
  };
}

export default function StatementTable({ bill, editable = false, onSplitChange, isSaving = false }) {
  const [elecSplit, setElecSplit] = useState(Math.round(bill.electricity_split * 100));
  const [otherSplit, setOtherSplit] = useState(Math.round(bill.other_split * 100));
  const [copied, setCopied] = useState(false);

  const enmax = bill.line_items.filter((i) => i.category === "enmax");
  const city = bill.line_items.filter((i) => i.category === "city");
  const hasSplitChanged =
    elecSplit !== Math.round(bill.electricity_split * 100) ||
    otherSplit !== Math.round(bill.other_split * 100);

  // Subtotal calculations
  const elecRawTotal = enmax.reduce((sum, item) => sum + item.full_amount, 0);
  const elecTenantTotal = enmax.reduce((sum, item) => sum + item.tenant_share, 0);
  const elecLandlordTotal = elecRawTotal - elecTenantTotal;

  const otherRawTotal = city.reduce((sum, item) => sum + item.full_amount, 0);
  const otherTenantTotal = city.reduce((sum, item) => sum + item.tenant_share, 0);
  const otherLandlordTotal = otherRawTotal - otherTenantTotal;

  const handleCopySummary = () => {
    const tenantTotal = bill.totals?.tenant_total?.toFixed(2);
    const landlordTotal = (bill.totals?.landlord_total ?? 0).toFixed(2);
    const grandTotal = bill.totals?.grand_total?.toFixed(2);

    const enmaxBreakdown = enmax
      .map(
        (item) =>
          `  - ${item.label}: $${item.full_amount.toFixed(2)} (${Math.round(
            item.tenant_split * 100
          )}% split → Tenant: $${item.tenant_share.toFixed(2)})`
      )
      .join("\n");

    const cityBreakdown = city
      .map(
        (item) =>
          `  - ${item.label}: $${item.full_amount.toFixed(2)} (${Math.round(
            item.tenant_split * 100
          )}% split → Tenant: $${item.tenant_share.toFixed(2)})`
      )
      .join("\n");

    const text = `==================================
CALGARY UTILITY SPLIT INVOICE
==================================
Bill Date:   ${bill.bill_date}
Due Date:    ${bill.due_date}
Property:    39 Seton Parade SE, Calgary AB

----------------------------------
1. ENMAX CHARGES
----------------------------------
${enmaxBreakdown}
ENMAX Raw Total:     $${elecRawTotal.toFixed(2)}
ENMAX Tenant Share:  $${elecTenantTotal.toFixed(2)}
ENMAX Landlord Share:$${elecLandlordTotal.toFixed(2)}

----------------------------------
2. CITY OF CALGARY CHARGES
----------------------------------
${cityBreakdown}
City Raw Total:      $${otherRawTotal.toFixed(2)}
City Tenant Share:   $${otherTenantTotal.toFixed(2)}
City Landlord Share: $${otherLandlordTotal.toFixed(2)}

==================================
TOTAL BILL SUMMARY
==================================
Grand Raw Total:     $${grandTotal}
Tenant Share Total:  $${tenantTotal}
Landlord Share Total: $${landlordTotal}

Generated via Calgary Utility splitter.
==================================`;

    const fallbackCopy = (val) => {
      const textArea = document.createElement("textarea");
      textArea.value = val;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Fallback copy failed", err);
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          fallbackCopy(text);
        });
    } else {
      fallbackCopy(text);
    }
  };

  const row = (item) => {
    const isGst = item.label.toLowerCase().includes("gst");
    const pct = Math.round(item.tenant_split * 100);
    const details = getUtilityDetails(item.label);
    const IconComponent = details.icon;

    const landlordShare = item.full_amount - item.tenant_share;

    return (
      <tr
        key={item.label}
        className="border-b border-border transition-colors hover:bg-slate-50/50 dark:hover:bg-white/[0.01]"
      >
        {/* Service Details with Custom Icon */}
        <td className="px-5 py-4 flex items-center gap-3">
          {isGst ? (
            <div className="w-9 h-9 shrink-0" />
          ) : (
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border ${details.iconColor} transition-colors duration-300`}
            >
              <IconComponent size={16} />
            </div>
          )}
          <div>
            <div
              className={`text-[13px] font-bold text-text-primary transition-colors duration-300 ${
                isGst ? "italic text-text-muted font-normal" : ""
              }`}
            >
              {details.name}
            </div>
            {!isGst && (
              <div className="text-[10px] text-text-muted font-extrabold tracking-wider mt-0.5 transition-colors duration-300">
                {details.sub}
              </div>
            )}
          </div>
        </td>

        {/* Provider Badge */}
        <td className="px-5 py-4">
          {isGst ? (
            <span className="text-text-muted text-[13px]">—</span>
          ) : item.category === "enmax" ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase bg-amber-50 text-amber-700 border border-amber-200/50 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30">
              <Zap size={10} className="fill-current" /> ENMAX
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase bg-teal-50 text-teal-700 border border-teal-200/50 dark:bg-teal-950/20 dark:text-teal-400 dark:border-teal-900/30">
              <Building size={10} /> City of Calgary
            </span>
          )}
        </td>

        {/* Raw Cost */}
        <td className="px-5 py-4 font-mono text-[13px] font-bold text-text-primary text-right transition-colors duration-300">
          ${item.full_amount.toFixed(2)}
        </td>

        {/* Split Ratio Capsules */}
        <td className="px-5 py-4">
          {isGst ? (
            <span className="text-text-muted text-[13px]">—</span>
          ) : pct > 0 ? (
            <div className="inline-flex items-center p-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 rounded-full select-none transition-colors duration-300">
              {pct === 80 ? (
                <>
                  <span className="bg-sky-600 text-white px-2 py-0.5 rounded-full text-[9.5px] font-bold shadow-sm transition-all">
                    80% Tenant
                  </span>
                  <span className="text-[9.5px] font-semibold text-text-muted px-2 py-0.5">
                    20% LL
                  </span>
                </>
              ) : pct === 70 ? (
                <>
                  <span className="bg-violet text-white px-2 py-0.5 rounded-full text-[9.5px] font-bold shadow-sm transition-all">
                    70% Tenant
                  </span>
                  <span className="text-[9.5px] font-semibold text-text-muted px-2 py-0.5">
                    30% LL
                  </span>
                </>
              ) : (
                <>
                  <span className="bg-accent text-white px-2 py-0.5 rounded-full text-[9.5px] font-bold shadow-sm transition-all">
                    {pct}% Tenant
                  </span>
                  <span className="text-[9.5px] font-semibold text-text-muted px-2 py-0.5">
                    {100 - pct}% LL
                  </span>
                </>
              )}
            </div>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-bold bg-rose-50 text-rose-600 border border-rose-100 dark:bg-rose-950/20 dark:text-rose-400 dark:border-rose-900/30">
              • 0% (Excluded)
            </span>
          )}
        </td>

        {/* Tenant Share (Blue Accent) */}
        <td className="px-5 py-4 text-right font-mono text-[14px] font-extrabold text-sky-600 dark:text-sky-400 transition-colors duration-300">
          ${item.tenant_share.toFixed(2)}
        </td>

        {/* Landlord Share (Purple Accent) */}
        <td className="px-5 py-4 text-right font-mono text-[14px] font-extrabold text-violet dark:text-violet-light transition-colors duration-300">
          ${landlordShare.toFixed(2)}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Configure Splitting Panel (If unfinalized & editable) */}
      {editable && !bill.finalized && (
        <div className="glass-panel p-5 flex items-center gap-5 flex-wrap">
          <span className="text-[13px] font-bold text-text-primary tracking-tight">
            Configure Statement splits:
          </span>
          <div className="flex gap-4 flex-wrap">
            {[
              ["Electricity Split", elecSplit, setElecSplit],
              ["Other Split", otherSplit, setOtherSplit],
            ].map(([lbl, val, set]) => (
              <label key={lbl} className="text-[12.5px] flex items-center gap-2 font-medium text-text-secondary">
                <span>{lbl}:</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={val}
                  onChange={(e) => set(Number(e.target.value))}
                  className="w-14 px-2 py-1 bg-surface-2 border border-border rounded-lg text-[13px] font-bold text-center text-text-primary focus:ring-1 focus:ring-accent focus:border-accent outline-none"
                />
                <span className="text-text-muted font-bold">%</span>
              </label>
            ))}
          </div>
          {hasSplitChanged && (
            <button
              onClick={() => onSplitChange && onSplitChange(elecSplit / 100, otherSplit / 100)}
              disabled={isSaving}
              className="glass-button glass-button-primary px-4 py-2 text-[12.5px] font-bold ml-auto"
            >
              {isSaving ? "Saving…" : "Recalculate splits"}
            </button>
          )}
        </div>
      )}

      {/* Main Invoices Table Card */}
      <div className="glass-panel overflow-hidden">
        <div className="px-5 py-4 border-b border-border bg-slate-50/50 dark:bg-slate-900/30 flex justify-between items-center flex-wrap gap-2 transition-colors duration-300">
          <div>
            <h3 className="font-display text-[15px] font-extrabold tracking-tight text-text-primary uppercase">
              Tracked Utility Invoices
            </h3>
            <p className="text-[11px] text-text-muted mt-0.5 transition-colors duration-300">
              Breakdown of current parsed statement lines
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[12.5px] font-extrabold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/30 rounded-lg shadow-sm transition-all duration-300">
            <Calendar size={13} className="stroke-[2.5]" />
            Due date: {bill.due_date}
          </span>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border bg-slate-100/80 dark:bg-slate-900/60 transition-colors duration-300">
                {[
                  "SERVICE DETAILS",
                  "PROVIDER",
                  "RAW COST",
                  "SPLIT RATIO",
                  "TENANT SHARE",
                  "LANDLORD SHARE",
                ].map((h, i) => (
                  <th
                    key={h}
                    className={`px-5 py-3 text-[10px] text-text-secondary uppercase tracking-[0.08em] font-extrabold transition-colors duration-300 ${
                      i === 0
                        ? "text-left"
                        : i === 1 || i === 3
                        ? "text-left"
                        : "text-right"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enmax.length > 0 && (
                <>
                  <tr className="bg-slate-100/10 dark:bg-slate-950/20 transition-colors duration-300">
                    <td
                      colSpan={6}
                      className="px-5 py-2 font-display font-extrabold text-[10px] text-amber-600 dark:text-amber-400 tracking-[0.07em] uppercase border-y border-border"
                    >
                      ENMAX Charges
                    </td>
                  </tr>
                  {enmax.map(row)}
                </>
              )}

              {city.length > 0 && (
                <>
                  <tr className="bg-slate-100/10 dark:bg-slate-950/20 transition-colors duration-300">
                    <td
                      colSpan={6}
                      className="px-5 py-2 font-display font-extrabold text-[10px] text-teal-600 dark:text-teal-400 tracking-[0.07em] uppercase border-y border-border"
                    >
                      City of Calgary Charges
                    </td>
                  </tr>
                  {city.map(row)}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Total Statement Summary Panel */}
      <div className="glass-panel p-5 bg-surface-2 border-border flex items-center justify-between flex-wrap gap-5 transition-colors duration-300">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center text-[18px]">
            💼
          </div>
          <div>
            <h4 className="text-[12px] font-extrabold uppercase text-text-secondary tracking-wider leading-none">
              Total Statement Amount
            </h4>
            <div className="flex items-baseline gap-2 mt-1.5">
              <span className="font-mono text-[24px] font-black text-text-primary tracking-tight leading-none">
                ${(elecRawTotal + otherRawTotal).toFixed(2)}
              </span>
              <span className="text-[11px] text-text-secondary font-semibold">
                (Current Charges)
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-8 flex-wrap">
          <div className="flex flex-col">
            <span className="text-[10px] font-extrabold uppercase text-sky-600 dark:text-sky-400 tracking-wider">
              Tenant Share Total
            </span>
            <span className="font-mono text-[18px] font-extrabold text-sky-600 dark:text-sky-400 mt-0.5">
              ${(elecTenantTotal + otherTenantTotal).toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-extrabold uppercase text-violet dark:text-violet-light tracking-wider">
              Landlord Share Total
            </span>
            <span className="font-mono text-[18px] font-bold text-violet dark:text-violet-light mt-0.5">
              ${(elecLandlordTotal + otherLandlordTotal).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Grid of Sub-totals Cards & Copy Action panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Electricity Subtotal Card */}
        <div className="glass-panel p-5 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-sky-600" />
          <div>
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-[10.5px] font-extrabold uppercase text-text-muted tracking-wider">
                ENMAX Sub-total
              </span>
              <span className="bg-sky-50 text-sky-600 dark:bg-sky-950/30 dark:text-sky-400 text-[9px] font-bold px-2 py-0.5 rounded-full border border-sky-100 dark:border-sky-900/30">
                Split {Math.round(bill.electricity_split * 100)}/{100 - Math.round(bill.electricity_split * 100)}
              </span>
            </div>
            <div className="font-mono text-[22px] font-bold text-text-primary leading-tight">
              ${elecRawTotal.toFixed(2)}
            </div>
          </div>
          <div className="mt-4 pt-3.5 border-t border-border flex justify-between text-[12.5px] transition-colors duration-300">
            <span className="font-medium text-sky-600 dark:text-sky-400">
              Tenant: <strong className="font-extrabold">${elecTenantTotal.toFixed(2)}</strong>
            </span>
            <span className="font-medium text-text-muted">
              LL: <strong className="font-bold">${elecLandlordTotal.toFixed(2)}</strong>
            </span>
          </div>
        </div>

        {/* Other Utilities Subtotal Card */}
        <div className="glass-panel p-5 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-violet" />
          <div>
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-[10.5px] font-extrabold uppercase text-text-muted tracking-wider">
                City Sub-total
              </span>
              <span className="bg-violet-light/10 text-violet dark:text-violet-light text-[9px] font-bold px-2 py-0.5 rounded-full border border-violet/10">
                Split {Math.round(bill.other_split * 100)}/{100 - Math.round(bill.other_split * 100)}
              </span>
            </div>
            <div className="font-mono text-[22px] font-bold text-text-primary leading-tight">
              ${otherRawTotal.toFixed(2)}
            </div>
          </div>
          <div className="mt-4 pt-3.5 border-t border-border flex justify-between text-[12.5px] transition-colors duration-300">
            <span className="font-medium text-violet dark:text-violet-light">
              Tenant: <strong className="font-extrabold">${otherTenantTotal.toFixed(2)}</strong>
            </span>
            <span className="font-medium text-text-muted">
              LL: <strong className="font-bold">${otherLandlordTotal.toFixed(2)}</strong>
            </span>
          </div>
        </div>

        {/* Copy Invoice summary Card */}
        <div className="glass-panel p-5 flex flex-col justify-between relative overflow-hidden bg-sky-500/5 border-sky-500/10">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-sky-500/20" />
          <div>
            <div className="flex justify-between items-start mb-2">
              <span className="text-[10.5px] font-extrabold uppercase text-sky-600 dark:text-sky-400 tracking-wider">
                Copy Tenant Summary Invoice
              </span>
              <Copy size={13} className="text-sky-500 shrink-0" />
            </div>
            <p className="text-[11px] text-text-secondary leading-relaxed mt-1">
              Export a beautifully formatted text copy of this billing period breakdown with transparent splits.
            </p>
          </div>
          <div className="mt-4 pt-1.5">
            <button
              onClick={handleCopySummary}
              className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12.5px] font-bold text-white transition-all shadow-sm active:scale-95 ${
                copied
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-sky-600 hover:bg-sky-700 shadow-sky-600/10"
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle2 size={13} /> Invoice Copied!
                </>
              ) : (
                <>
                  <Copy size={13} /> Copy Split Invoice
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
